import React, { useState, useEffect, useRef } from 'react';

/**
 * まとめて入力できる日付・時刻フィールド。
 * - テキストで「1115」「11/15」「2026/11/15」等を打つと YYYY-MM-DD に自動整形。
 * - 右端の📅/🕐ボタンでネイティブの日付/時刻ピッカーも使える（併用）。
 * 値は日付 'YYYY-MM-DD'、時刻 'HH:MM' の正規形で親に渡す。
 */

const inputStyle: React.CSSProperties = {
  border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '9px 12px', fontSize: 14, outline: 'none', width: '100%', boxSizing: 'border-box',
};

// 「1115」「11/15」「2026-11-15」等 → 'YYYY-MM-DD'（解釈できなければ null）
export function parseFlexibleDate(raw: string, baseYear: number): string | null {
  const t = raw.trim();
  if (!t) return null;
  const nums = t.split(/[^0-9]+/).filter(Boolean);
  let y: number, m: number, d: number;
  if (nums.length >= 3) {
    // Y M D（区切りあり）
    [y, m, d] = [Number(nums[0]), Number(nums[1]), Number(nums[2])];
    if (y < 100) y += 2000;
  } else if (nums.length === 2) {
    // M D（年は基準年）
    y = baseYear; m = Number(nums[0]); d = Number(nums[1]);
  } else {
    // 区切りなしの連続数字
    const s = nums[0] ?? '';
    if (s.length === 8) { y = +s.slice(0, 4); m = +s.slice(4, 6); d = +s.slice(6, 8); }
    else if (s.length === 6) { y = 2000 + +s.slice(0, 2); m = +s.slice(2, 4); d = +s.slice(4, 6); }
    else if (s.length === 4) { y = baseYear; m = +s.slice(0, 2); d = +s.slice(2, 4); }
    else if (s.length === 3) { y = baseYear; m = +s.slice(0, 1); d = +s.slice(1, 3); }
    else return null;
  }
  if (!m || !d || m < 1 || m > 12 || d < 1 || d > 31) return null;
  const iso = `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  const dt = new Date(`${iso}T00:00:00`);
  if (isNaN(dt.getTime()) || dt.getMonth() + 1 !== m || dt.getDate() !== d) return null; // 2/30等を弾く
  return iso;
}

// 「1445」「14:45」「930」「9」→ 'HH:MM'（解釈できなければ null）
export function parseFlexibleTime(raw: string): string | null {
  const t = raw.trim();
  if (!t) return null;
  let h: number, mi: number;
  if (t.includes(':')) {
    const [a, b] = t.split(':');
    h = Number(a); mi = Number(b ?? '0');
  } else {
    const s = t.replace(/[^0-9]/g, '');
    if (s.length <= 2) { h = Number(s); mi = 0; }
    else if (s.length === 3) { h = +s.slice(0, 1); mi = +s.slice(1, 3); }
    else { h = +s.slice(0, 2); mi = +s.slice(2, 4); }
  }
  if (isNaN(h) || isNaN(mi) || h < 0 || h > 23 || mi < 0 || mi > 59) return null;
  return `${String(h).padStart(2, '0')}:${String(mi).padStart(2, '0')}`;
}

const fmtDateDisplay = (iso: string) => iso ? iso.replace(/-/g, '/') : '';

export const SmartDateInput: React.FC<{
  value: string;                 // 'YYYY-MM-DD'
  onChange: (v: string) => void;
  baseYear?: number;             // 年省略時に使う年（既定: 今年）
  style?: React.CSSProperties;
  placeholder?: string;
}> = ({ value, onChange, baseYear, style, placeholder }) => {
  const [text, setText] = useState(fmtDateDisplay(value));
  const dateRef = useRef<HTMLInputElement>(null);
  useEffect(() => { setText(fmtDateDisplay(value)); }, [value]);
  const commit = () => {
    if (!text.trim()) { onChange(''); return; }
    const parsed = parseFlexibleDate(text, baseYear ?? new Date().getFullYear());
    if (parsed) { onChange(parsed); setText(fmtDateDisplay(parsed)); }
    else setText(fmtDateDisplay(value)); // 解釈不可は元に戻す
  };
  return (
    <div style={{ position: 'relative', ...style }}>
      <input
        type="text" inputMode="numeric" value={text}
        onChange={e => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); commit(); } }}
        placeholder={placeholder ?? '例: 1115 / 2026/11/15'}
        style={{ ...inputStyle, paddingRight: 36 }}
      />
      <button type="button" onClick={() => dateRef.current?.showPicker?.()}
        title="カレンダーから選ぶ"
        style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'none', cursor: 'pointer', fontSize: 15, padding: 2 }}>📅</button>
      <input ref={dateRef} type="date" value={value} onChange={e => onChange(e.target.value)}
        style={{ position: 'absolute', right: 6, top: 0, width: 1, height: 1, opacity: 0, pointerEvents: 'none' }} tabIndex={-1} />
    </div>
  );
};

export const SmartTimeInput: React.FC<{
  value: string;                 // 'HH:MM'
  onChange: (v: string) => void;
  style?: React.CSSProperties;
  placeholder?: string;
}> = ({ value, onChange, style, placeholder }) => {
  const [text, setText] = useState(value);
  const timeRef = useRef<HTMLInputElement>(null);
  useEffect(() => { setText(value); }, [value]);
  const commit = () => {
    if (!text.trim()) { onChange(''); return; }
    const parsed = parseFlexibleTime(text);
    if (parsed) { onChange(parsed); setText(parsed); }
    else setText(value);
  };
  return (
    <div style={{ position: 'relative', ...style }}>
      <input
        type="text" inputMode="numeric" value={text}
        onChange={e => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); commit(); } }}
        placeholder={placeholder ?? '例: 1445'}
        style={{ ...inputStyle, paddingRight: 36 }}
      />
      <button type="button" onClick={() => timeRef.current?.showPicker?.()}
        title="時計から選ぶ"
        style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'none', cursor: 'pointer', fontSize: 15, padding: 2 }}>🕐</button>
      <input ref={timeRef} type="time" value={value} onChange={e => onChange(e.target.value)}
        style={{ position: 'absolute', right: 6, top: 0, width: 1, height: 1, opacity: 0, pointerEvents: 'none' }} tabIndex={-1} />
    </div>
  );
};
